import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { fromMinor, moneyShort, toMinor } from "@/lib/format";
import { ROLE_LABEL, type StaffLite } from "@/lib/types";
import { Button, Field, Input, Modal, cx, useConfirm, useToast } from "./ui";
import { Card } from "./Layout";

export type Level = "cashier" | "supervisor" | "manager";
export type TillRole = { id: string; label: string; base: Level; discount: number | null; refund: number | null };
export const LEVEL_SUB: Record<Level, string> = {
  cashier: "Sells. A price change or a bigger discount needs a PIN",
  supervisor: "Approves price changes, pay-outs and over / short",
  manager: "Approves anything, any discount or refund",
};

/** Till roles the shop added, and how many active staff have each. */
export function useTillRoles() {
  return useQuery({ queryKey: ["till-roles"], queryFn: () => api<{ roles: TillRole[]; people: Record<string, number> }>(till("/admin/till-roles")) });
}
/** The name to show for a person's till role. */
export const roleName = (p: Pick<StaffLite, "role" | "customRole">, roles: TillRole[] | undefined) => (p.customRole && roles?.find((r) => r.id === p.customRole)?.label) || ROLE_LABEL[p.role];

/** Roles & permissions: the till roles the shop added, with Add / Edit / Remove. */
export function YourTillRoles({ limits }: { limits?: { discount: Record<Level, number>; refund: Record<Level, number | null> } }) {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const q = useTillRoles();
  const roles = q.data?.roles ?? [];
  const [edit, setEdit] = useState<TillRole | "new" | null>(null);
  const save = async (next: TillRole[], done: string) => {
    try {
      await api(till("/admin/till-roles"), { method: "PUT", body: { roles: next } });
      toast(done);
      void qc.invalidateQueries({ queryKey: ["till-roles"] });
      void qc.invalidateQueries({ queryKey: ["staff"] });
      return true;
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
      return false;
    }
  };
  const lim = (r: TillRole) => ({
    discount: r.discount ?? limits?.discount[r.base] ?? null,
    refund: r.refund != null ? r.refund : limits ? limits.refund[r.base] : undefined,
  });
  return (
    <Card title="Your till roles" right={<Button tone="primary" onClick={() => setEdit("new")}>Add till role</Button>}>
      {roles.length ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead><tr>{["Role", "Works like", "Discount limit", "Refund limit", "People", ""].map((h) => <th key={h} className="border-b border-slate-100 px-4 py-2.5 text-left text-xs font-semibold text-slate-500">{h}</th>)}</tr></thead>
            <tbody>
              {roles.map((r) => {
                const l = lim(r);
                return (
                  <tr key={r.id}>
                    <td className="border-b border-slate-100 px-4 py-3 font-semibold">{r.label}</td>
                    <td className="border-b border-slate-100 px-4 py-3">{ROLE_LABEL[r.base]}</td>
                    <td className="border-b border-slate-100 px-4 py-3">{l.discount == null ? "-" : `Up to ${l.discount}%`}{r.discount == null ? <span className="block text-xs text-slate-500">Same as {ROLE_LABEL[r.base]}</span> : null}</td>
                    <td className="border-b border-slate-100 px-4 py-3">{l.refund === undefined ? "-" : l.refund == null ? "Any" : `Up to ${moneyShort(l.refund)}`}{r.refund == null ? <span className="block text-xs text-slate-500">Same as {ROLE_LABEL[r.base]}</span> : null}</td>
                    <td className="border-b border-slate-100 px-4 py-3">{q.data?.people[r.id] ?? 0}</td>
                    <td className="whitespace-nowrap border-b border-slate-100 px-4 py-3 text-right">
                      <Button size="sm" className="mr-2" onClick={() => setEdit(r)}>Edit</Button>
                      <Button size="sm" tone="danger" onClick={async () => { if (await confirm({ title: `Remove the ${r.label} role?`, body: "You can add it again later.", ok: "Remove role", danger: true })) void save(roles.filter((x) => x.id !== r.id), `${r.label} removed`); }}>Remove</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="px-5 pb-5 text-sm text-slate-500">{q.isLoading ? "Loading…" : "None yet. Add one, like \"Senior cashier\", and choose which role it works like. Then pick it when you add staff."}</p>
      )}
      {edit ? <TillRoleModal role={edit === "new" ? null : edit} taken={roles.filter((x) => edit === "new" || x.id !== edit.id).map((x) => x.label.toLowerCase())} onClose={() => setEdit(null)} onSave={async (r) => { const next = edit === "new" ? [...roles, r] : roles.map((x) => (x.id === r.id ? r : x)); if (await save(next, edit === "new" ? `${r.label} added` : `${r.label} saved`)) setEdit(null); }} /> : null}
    </Card>
  );
}

function TillRoleModal({ role, taken, onClose, onSave }: { role: TillRole | null; taken: string[]; onClose: () => void; onSave: (r: TillRole) => void }) {
  const [label, setLabel] = useState(role?.label ?? "");
  const [base, setBase] = useState<Level>(role?.base ?? "cashier");
  const [discount, setDiscount] = useState(role?.discount == null ? "" : String(role.discount));
  const [refund, setRefund] = useState(role?.refund == null ? "" : fromMinor(role.refund));
  const clash = taken.includes(label.trim().toLowerCase()) || ["cashier", "supervisor", "manager", "till manager", "yard staff"].includes(label.trim().toLowerCase());
  const ok = !!label.trim() && !clash;
  return (
    <Modal title={role ? `Edit ${role.label}` : "Add a till role"} sub="For staff who sign in at the till with a PIN." onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button tone="primary" disabled={!ok} onClick={() => onSave({ id: role?.id ?? "", label: label.trim(), base, discount: discount === "" ? null : Number(discount), refund: refund === "" ? null : toMinor(refund) })}>{role ? "Save" : "Add role"}</Button></>}>
      <div className="flex flex-col gap-4">
        <Field label="Name" hint={clash ? "A till role already has this name" : "What it's called on the till and in reports"}>
          <Input autoFocus value={label} maxLength={40} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Senior cashier" />
        </Field>
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold">Works like</span>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {(["cashier", "supervisor", "manager"] as Level[]).map((id) => (
              <button key={id} type="button" aria-pressed={base === id} onClick={() => setBase(id)} className={cx("flex flex-col items-start gap-0.5 rounded-xl border-[1.5px] px-3 py-2.5 text-left", base === id ? "border-brand bg-brand-tint" : "border-slate-200 bg-white")}>
                <b className="text-sm">{ROLE_LABEL[id]}</b>
                <span className="text-xs text-slate-500">{LEVEL_SUB[id]}</span>
              </button>
            ))}
          </div>
          <span className="text-xs text-slate-500">It can do and approve exactly what this role can at the till.</span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Discount limit (%)" hint="Leave blank to use the same as the role it works like">
            <Input inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value.replace(/[^\d.]/g, ""))} placeholder="Same" />
          </Field>
          <Field label="Refund limit (₵)" hint="Leave blank to use the same as the role it works like">
            <Input inputMode="decimal" value={refund} onChange={(e) => setRefund(e.target.value.replace(/[^\d.]/g, ""))} placeholder="Same" />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
